import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { enviosApi } from '../api';
import type { EnvioInformeRequest } from '../types';

const QUERY_KEY_BASE = ['envios'] as const;

export const enviosKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
};

/**
 * Refetch automático cada 10s mientras haya envíos con status_global "pending",
 * para que la UI se actualice cuando el worker termina de procesar.
 */
export function useEnviosByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? enviosKeys.byProtocolo(uidProtocolo) : enviosKeys.all,
    queryFn: ({ signal }) => enviosApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasPending = data.some((e) => e.status_global === 'pending');
      return hasPending ? 10_000 : false;
    },
  });
}

export function useEnviarInformeParcial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      body,
    }: {
      uidProtocolo: string;
      body?: EnvioInformeRequest;
    }) => enviosApi.enviarParcial(uidProtocolo, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: enviosKeys.byProtocolo(variables.uidProtocolo),
      });
    },
  });
}

export function useEnviarInformeFinal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      body,
    }: {
      uidProtocolo: string;
      body?: EnvioInformeRequest;
    }) => enviosApi.enviarFinal(uidProtocolo, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: enviosKeys.byProtocolo(variables.uidProtocolo),
      });
    },
  });
}

export function useReintentarEnvio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uidEnvio: string) => enviosApi.reintentar(uidEnvio),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: enviosKeys.byProtocolo(data.uid_protocolo),
      });
    },
  });
}
